import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Wifi, RefreshCw, Search as SearchIcon, Activity, Router as RouterIcon,
  Users, Ticket, HardDrive, Clock, Play, Pause,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useApp } from '../contexts/AppContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDuration = (sec) => {
  const s = Number(sec || 0);
  if (s <= 0) return '0m';
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const parts = [];
  if (d) parts.push(`${d}d`);
  if (h) parts.push(`${h}h`);
  if (m || (!d && !h)) parts.push(`${m}m`);
  return parts.join(' ');
};

const fmtBytes = (b) => {
  const n = Number(b || 0);
  if (n < 1024) return `${n} B`;
  if (n < 1024 ** 2) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 ** 3) return `${(n / (1024 ** 2)).toFixed(1)} MB`;
  return `${(n / (1024 ** 3)).toFixed(2)} GB`;
};

export default function LiveSessions() {
  const { token } = useAuth();
  const { selectedBranch, selectedRouter } = useApp(); /* TENANT_ISO_LIVE_SESSIONS_ROUTER_V1 */
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ stats: { total: 0, voucher_count: 0, pppoe_count: 0, unknown_count: 0, total_bytes: 0 }, sessions: [] });
  const [typeFilter, setTypeFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [showTrend, setShowTrend] = useState(true);
  const [trend, setTrend] = useState([]);
  const [lastRefresh, setLastRefresh] = useState(null);
  const intervalRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const [searchDebounced, setSearchDebounced] = useState('');
  const [pageSize, setPageSize] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);

  const fetchData = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const params = {};
      if (typeFilter && typeFilter !== 'all') params.type = typeFilter;
      if (searchDebounced) params.search = searchDebounced;
      if (selectedBranch && selectedBranch !== 'all') params.branch_id = selectedBranch;
      if (selectedRouter && selectedRouter !== 'all') params.router_id = selectedRouter;
      const res = await axios.get(`${API_URL}/api/live-sessions`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data || { stats: {}, sessions: [] });
      setLastRefresh(new Date());
    } catch (e) {
      if (!silent) toast.error(e.response?.data?.detail || 'Gagal load sesi');
    } finally {
      if (!silent) setLoading(false);
    }
  };
  const fetchTrend = async () => {
    try {
      const trendParams = new URLSearchParams({ hours: '24' });
      if (selectedBranch && selectedBranch !== 'all') trendParams.append('branch_id', selectedBranch);
      if (selectedRouter && selectedRouter !== 'all') trendParams.append('router_id', selectedRouter);
      const res = await axios.get(`${API_URL}/api/live-sessions/trend?${trendParams.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTrend(res.data?.data || []);
    } catch (e) {
      // silent
    }
  };

  // Debounce search
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => setSearchDebounced(search), 500);
    return () => searchDebounceRef.current && clearTimeout(searchDebounceRef.current);
  }, [search]);

  useEffect(() => {
    fetchData();
    fetchTrend();
    setCurrentPage(1);
  }, [typeFilter, searchDebounced, selectedBranch, selectedRouter]);

  // Auto-refresh every 30s
  useEffect(() => {
    if (!autoRefresh) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      return undefined;
    }
    intervalRef.current = setInterval(() => fetchData(true), 30000);
    return () => intervalRef.current && clearInterval(intervalRef.current);
  }, [autoRefresh, typeFilter, searchDebounced, selectedBranch, selectedRouter]);

  const sessions = data.sessions || [];
  const stats = data.stats || {};

  return (
    <div className="p-3 sm:p-6 space-y-4" data-testid="live-sessions-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2 text-slate-800 dark:text-slate-100">
            <Activity className="w-6 h-6 text-emerald-600 animate-pulse" /> Live Session Monitor
          </h1>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            {lastRefresh ? `Diperbarui: ${lastRefresh.toLocaleTimeString('id-ID')}` : 'Memuat...'} · Auto-refresh 30s
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setAutoRefresh(v => !v)}
            variant="outline"
            size="sm"
            className={autoRefresh ? 'border-emerald-500 text-emerald-600' : ''}
            data-testid="live-auto-refresh-btn"
          >
            {autoRefresh ? <Play className="w-4 h-4 mr-1" /> : <Pause className="w-4 h-4 mr-1" />}
            Auto {autoRefresh ? 'ON' : 'OFF'}
          </Button>
          <Button onClick={() => fetchData()} variant="outline" size="sm" disabled={loading} data-testid="live-refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="border-emerald-200 dark:border-emerald-800/40 bg-emerald-50/50 dark:bg-emerald-950/20">
          <CardContent className="p-3">
            <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-300">
              <Wifi className="w-4 h-4" />
              <span className="text-xs font-medium">Total Online</span>
            </div>
            <div className="text-2xl font-bold text-emerald-700 dark:text-emerald-300 mt-1" data-testid="stat-total-online">{stats.total || 0}</div>
          </CardContent>
        </Card>
        <Card className="border-purple-200 dark:border-purple-800/40 bg-purple-50/50 dark:bg-purple-950/20">
          <CardContent className="p-3">
            <div className="flex items-center gap-2 text-purple-700 dark:text-purple-300">
              <Ticket className="w-4 h-4" />
              <span className="text-xs font-medium">Voucher</span>
            </div>
            <div className="text-2xl font-bold text-purple-700 dark:text-purple-300 mt-1">{stats.voucher_count || 0}</div>
          </CardContent>
        </Card>
        <Card className="border-blue-200 dark:border-blue-800/40 bg-blue-50/50 dark:bg-blue-950/20">
          <CardContent className="p-3">
            <div className="flex items-center gap-2 text-blue-700 dark:text-blue-300">
              <Users className="w-4 h-4" />
              <span className="text-xs font-medium">Pelanggan PPPoE</span>
            </div>
            <div className="text-2xl font-bold text-blue-700 dark:text-blue-300 mt-1">{stats.pppoe_count || 0}</div>
          </CardContent>
        </Card>
        <Card className="border-amber-200 dark:border-amber-800/40 bg-amber-50/50 dark:bg-amber-950/20">
          <CardContent className="p-3">
            <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
              <HardDrive className="w-4 h-4" />
              <span className="text-xs font-medium" title="Data byte hanya tersedia untuk voucher (radacct). PPPoE dari mikrotik_pppoe_active tidak simpan bytes.">Bandwidth Voucher</span>
            </div>
            <div className="text-2xl font-bold text-amber-700 dark:text-amber-300 mt-1">{fmtBytes(stats.total_bytes)}</div>
          </CardContent>
        </Card>
      </div>

      {/* Trend Chart */}
      {showTrend && trend.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2 text-slate-700 dark:text-slate-200">
                <TrendingUp className="w-4 h-4 text-emerald-600" /> Trend Sesi Baru 24 Jam Terakhir
              </CardTitle>
              <button
                onClick={() => setShowTrend(false)}
                className="text-xs text-slate-500 hover:text-slate-700"
                data-testid="toggle-trend-btn"
              >Sembunyikan</button>
            </div>
          </CardHeader>
          <CardContent className="pt-1">
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={trend} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="hour" stroke="#64748b" fontSize={11} />
                <YAxis stroke="#64748b" fontSize={11} />
                <Tooltip
                  contentStyle={{ backgroundColor: "#0f172a", border: 0, borderRadius: 8, color: '#f1f5f9', fontSize: 12 }}
                  labelStyle={{ color: '#94a3b8' }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {typeFilter !== 'voucher' && (
                  <Line type="monotone" dataKey="pppoe" stroke="#3b82f6" strokeWidth={2} name="PPPoE" dot={{ r: 2 }} activeDot={{ r: 5 }} />
                )}
                {typeFilter !== 'pppoe' && (
                  <Line type="monotone" dataKey="voucher" stroke="#a855f7" strokeWidth={2} name="Voucher" dot={{ r: 2 }} activeDot={{ r: 5 }} />
                )}
              </LineChart>
            </ResponsiveContainer>
            <div className="text-[10px] text-slate-500 dark:text-slate-400 mt-2 text-center">
              *Data: sesi baru yang connect pada tiap jam (bukan total online saat ini)
            </div>
          </CardContent>
        </Card>
      )}
      {!showTrend && (
        <button
          onClick={() => { setShowTrend(true); fetchTrend(); }}
          className="text-xs text-emerald-600 hover:text-emerald-700 flex items-center gap-1"
        >
          <TrendingUp className="w-3 h-3" /> Tampilkan grafik trend
        </button>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-3">
          <div className="flex flex-wrap gap-2 items-center">
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="px-3 py-1.5 border border-slate-300 dark:border-slate-600 rounded-lg text-sm h-9 focus:ring-2 focus:ring-emerald-500 dark:bg-slate-800 dark:text-slate-200"
              data-testid="live-type-filter"
            >
              <option value="all">Semua Tipe ({stats.total || 0})</option>
              <option value="voucher">Voucher ({stats.voucher_count || 0})</option>
              <option value="pppoe">PPPoE ({stats.pppoe_count || 0})</option>
            </select>
            <div className="flex-1 min-w-[220px] relative">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cari username / nama / MAC / IP / router..."
                className="pl-9 pr-3 py-1.5 border border-slate-300 dark:border-slate-600 rounded-lg text-sm h-9 w-full focus:ring-2 focus:ring-emerald-500 dark:bg-slate-800 dark:text-slate-100"
                data-testid="live-search-input"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-xs" data-testid="live-sessions-table">
            <thead className="bg-slate-100 dark:bg-slate-800 sticky top-0">
              <tr>
                <th className="px-2 py-2 text-left font-semibold text-slate-700 dark:text-slate-200">Tipe</th>
                <th className="px-2 py-2 text-left font-semibold text-slate-700 dark:text-slate-200">Username</th>
                <th className="px-2 py-2 text-left font-semibold text-slate-700 dark:text-slate-200">Nama / Owner</th>
                <th className="px-2 py-2 text-left font-semibold text-slate-700 dark:text-slate-200">Cabang</th>
                <th className="px-2 py-2 text-left font-semibold text-slate-700 dark:text-slate-200">Agen</th>
                <th className="px-2 py-2 text-left font-semibold text-slate-700 dark:text-slate-200">Router</th>
                <th className="px-2 py-2 text-left font-semibold text-slate-700 dark:text-slate-200">MAC</th>
                <th className="px-2 py-2 text-left font-semibold text-slate-700 dark:text-slate-200">IP</th>
                <th className="px-2 py-2 text-right font-semibold text-slate-700 dark:text-slate-200">Durasi</th>
                <th className="px-2 py-2 text-right font-semibold text-slate-700 dark:text-slate-200">Bandwidth</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {loading && sessions.length === 0 && (
                <tr><td colSpan={10} className="px-3 py-8 text-center text-slate-400"><RefreshCw className="w-5 h-5 mx-auto animate-spin" /></td></tr>
              )}
              {!loading && sessions.length === 0 && (
                <tr><td colSpan={10} className="px-3 py-8 text-center text-slate-400">Tidak ada sesi aktif</td></tr>
              )}
              {sessions
                .slice((currentPage - 1) * pageSize, currentPage * pageSize)
                .map((s) => (
                <tr key={s.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                  <td className="px-2 py-2">
                    {s.type === 'voucher' && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-100 dark:bg-purple-950/40 text-purple-700 dark:text-purple-300 text-[10px] font-semibold"><Ticket className="w-3 h-3" /> Voucher</span>
                    )}
                    {s.type === 'pppoe' && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 text-[10px] font-semibold"><Users className="w-3 h-3" /> PPPoE</span>
                    )}
                    {s.type === 'unknown' && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 text-[10px] font-semibold">?</span>
                    )}
                  </td>
                  <td className="px-2 py-2 font-mono font-semibold text-slate-800 dark:text-slate-100">{s.username}</td>
                  <td className="px-2 py-2 text-slate-700 dark:text-slate-200">
                    {s.name || <span className="text-slate-400">-</span>}
                    {s.package_name && <div className="text-[10px] text-slate-500 dark:text-slate-400">{s.package_name}</div>}
                  </td>
                  <td className="px-2 py-2 text-slate-600 dark:text-slate-300">{s.branch_name || <span className="text-slate-400">-</span>}</td>
                  <td className="px-2 py-2">
                    {s.agent_name ? (
                      <span className="text-purple-700 dark:text-purple-300 font-medium">{s.agent_name}</span>
                    ) : <span className="text-slate-400">-</span>}
                  </td>
                  <td className="px-2 py-2">
                    {s.router_name ? (
                      <div className="flex items-center gap-1">
                        <RouterIcon className="w-3 h-3 text-slate-400" />
                        <div>
                          <div className="font-medium text-slate-700 dark:text-slate-200">{s.router_name}</div>
                          <div className="text-[10px] text-slate-500 dark:text-slate-400 font-mono">{s.nas_ip}</div>
                        </div>
                      </div>
                    ) : <span className="font-mono text-slate-500">{s.nas_ip}</span>}
                  </td>
                  <td className="px-2 py-2 font-mono text-[11px] text-indigo-600 dark:text-indigo-300">{s.mac}</td>
                  <td className="px-2 py-2 font-mono text-[11px] text-slate-600 dark:text-slate-300">{s.client_ip}</td>
                  <td className="px-2 py-2 text-right">
                    <span className="inline-flex items-center gap-1 font-medium text-slate-700 dark:text-slate-200">
                      <Clock className="w-3 h-3 text-slate-400" /> {fmtDuration(s.session_seconds)}
                    </span>
                  </td>
                  <td className="px-2 py-2 text-right font-medium text-amber-600 dark:text-amber-300">{fmtBytes(s.bytes_total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
        {sessions.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 px-3 py-2 border-t border-slate-100 dark:border-slate-800 text-xs">
            <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
              <span>Tampilkan</span>
              <select
                value={pageSize}
                onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                className="px-2 py-1 border border-slate-300 dark:border-slate-600 rounded text-xs bg-white dark:bg-slate-800 focus:ring-1 focus:ring-emerald-500"
                data-testid="live-page-size"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={250}>250</option>
                <option value={500}>500</option>
              </select>
              <span>entri</span>
            </div>
            <div className="text-slate-500 dark:text-slate-400">
              Menampilkan <strong className="text-slate-700 dark:text-slate-200">{Math.min((currentPage - 1) * pageSize + 1, sessions.length)}</strong>
              {' – '}
              <strong className="text-slate-700 dark:text-slate-200">{Math.min(currentPage * pageSize, sessions.length)}</strong>
              {' dari '}
              <strong className="text-slate-700 dark:text-slate-200">{sessions.length}</strong> sesi
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-2 py-1 border border-slate-300 dark:border-slate-600 rounded disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200"
                data-testid="live-page-prev"
              >‹ Prev</button>
              <span className="px-2 text-slate-600 dark:text-slate-300">
                {currentPage} / {Math.max(1, Math.ceil(sessions.length / pageSize))}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(Math.ceil(sessions.length / pageSize), p + 1))}
                disabled={currentPage >= Math.ceil(sessions.length / pageSize)}
                className="px-2 py-1 border border-slate-300 dark:border-slate-600 rounded disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200"
                data-testid="live-page-next"
              >Next ›</button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
